import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, ArrowRight, Loader2, X, Download, FileText } from "lucide-react";
import { listProcedures, procedureFacets } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Form-keys (returned by /api/procedure-forms) that exist as prefill generators.
// Cross-referenced against `procedure.roc_forms` so we only render the
// "Generate prefilled" panel for forms we can actually produce today.
const PROCEDURE_FORM_REGISTRY = {
  "STK-3": { key: "stk3", label: "STK-3 Indemnity Bond" },
  "STK-4": { key: "stk4", label: "STK-4 Affidavit" },
};

export default function Procedures() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ categories: [], kinds: [], total: 0 });
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [kind, setKind] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    procedureFacets().then((r) => setFacets(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      listProcedures({
        ...(q && { q }),
        ...(category && { category }),
        ...(kind && { kind }),
      })
        .then((r) => setItems(r.data || []))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, category, kind]);

  const groupedByCategory = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      (g[it.category] = g[it.category] || []).push(it);
    });
    return Object.entries(g).sort();
  }, [items]);

  const clear = () => { setQ(""); setCategory(""); setKind(""); };
  const activeFilters = !!(q || category || kind);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="procedures-page">
      <div className="mb-8">
        <div className="label-eyebrow mb-2">Reference · Companies Act 2013</div>
        <h1 className="font-heading text-4xl font-light tracking-tight mb-2">Procedures Library</h1>
        <p className="text-zinc-600 max-w-2xl text-sm leading-relaxed">
          {facets.total} structured procedures across {facets.categories.length} categories — each with applicable Sections,
          ROC e-Forms, statutory timelines and step-by-step checklists. Search or filter to find what you need.
        </p>
      </div>

      {/* Search + filters */}
      <div className="border border-zinc-200 bg-white p-6 mb-8" data-testid="procedures-filters">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-4 items-end">
          <label className="block">
            <span className="label-eyebrow block mb-1.5">Search</span>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. auditor, allotment, increase capital, MGT-14…"
                className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                data-testid="procedures-search"
              />
            </div>
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-1.5">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="procedures-category"
            >
              <option value="">All categories</option>
              {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-1.5">Resolution Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="procedures-kind"
            >
              <option value="">All kinds</option>
              {facets.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          {activeFilters && (
            <button onClick={clear} className="swiss-btn-ghost h-[38px]" data-testid="procedures-clear">
              <X size={14} /> Clear
            </button>
          )}
        </div>
        <div className="mt-3 text-xs text-zinc-500">
          {loading ? "Searching…" : `${items.length} ${items.length === 1 ? "match" : "matches"}`}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-zinc-500 py-12 text-center"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-16 text-center" data-testid="procedures-empty">
          <BookOpen size={32} strokeWidth={1.25} className="mx-auto text-zinc-400 mb-4" />
          <h2 className="font-heading text-xl font-medium mb-2">No procedures match</h2>
          <p className="text-zinc-500 text-sm">Try a different keyword or clear filters.</p>
        </div>
      ) : (
        <div className="space-y-10" data-testid="procedures-results">
          {groupedByCategory.map(([cat, list]) => (
            <div key={cat}>
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                {cat} <span className="text-zinc-400 font-mono-plex normal-case tracking-normal">· {list.length}</span>
              </div>
              <div className="border border-zinc-200">
                {list.map((p, i) => (
                  <ProcedureCard
                    key={p.key}
                    p={p}
                    isFirst={i === 0}
                    expanded={expanded === p.key}
                    onToggle={() => setExpanded(expanded === p.key ? null : p.key)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProcedureCard({ p, isFirst, expanded, onToggle }) {
  const sections = p.applicable_sections || [];
  const forms = p.roc_forms || [];
  return (
    <div className={`p-6 ${isFirst ? "" : "border-t border-zinc-100"} ${expanded ? "bg-zinc-50/50" : ""}`} data-testid={`procedure-card-${p.key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="font-heading font-medium text-lg">{p.title}</h3>
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-300 text-zinc-700">
              {p.resolution_kind?.toUpperCase()}
            </span>
            {p.notice_required && (
              <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-[#002FA7] text-[#002FA7]">
                Notice {p.notice_days}d
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-600 leading-relaxed">{p.description}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-xs">
            <MetaCell label="Sections" value={sections.length ? sections.join(", ") : "—"} />
            <MetaCell label="ROC Forms" value={forms.length ? forms.join(", ") : "—"} />
            <MetaCell label="Timeline" value={p.statutory_timeline || "—"} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button onClick={onToggle} className="swiss-btn-ghost text-sm" data-testid={`toggle-procedure-${p.key}`}>
            {expanded ? "Hide steps" : "View steps"}
          </button>
          <Link to={`/generate?type=${p.key}`} className="swiss-btn-primary text-sm" data-testid={`generate-from-${p.key}`}>
            Draft <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-zinc-200" data-testid={`procedure-steps-${p.key}`}>
          <div className="label-eyebrow mb-3">Step-by-step</div>
          <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-800 leading-relaxed">
            {(p.procedure || []).map((s, i) => <li key={`${p.key}-step-${i}`}>{s}</li>)}
          </ol>

          {/* Prefilled-forms panel — shows only when this procedure has at
              least one ROC form that ROC Vault can generate prefilled. */}
          <PrefilledFormsPanel procedure={p} />
        </div>
      )}
    </div>
  );
}


function PrefilledFormsPanel({ procedure }) {
  const supported = (procedure.roc_forms || []).filter((f) => PROCEDURE_FORM_REGISTRY[f]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [directorDin, setDirectorDin] = useState("");
  const [busy, setBusy] = useState("");  // form_key+fmt while downloading

  useEffect(() => {
    if (supported.length === 0) return;
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCompanies([]));
  }, [supported.length]);

  const selectedCompany = companies.find((c) => c.id === companyId);

  // Auto-pick first director when company changes
  useEffect(() => {
    if (!selectedCompany) { setDirectorDin(""); return; }
    const first = (selectedCompany.directors || []).find((d) => (d.din || "").trim());
    setDirectorDin(first ? first.din : "");
  }, [companyId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (supported.length === 0) return null;

  const handleDownload = async (formKey, fmt) => {
    if (!companyId) {
      toast.error("Select a company first");
      return;
    }
    setBusy(`${formKey}_${fmt}`);
    try {
      const url = `${API}/companies/${companyId}/procedure-forms/${formKey}?fmt=${fmt}` +
        (directorDin ? `&director_din=${directorDin}` : "");
      const r = await axios.get(url, { responseType: "blob", withCredentials: true });
      const mime = fmt === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      const blob = new Blob([r.data], { type: mime });
      const dl = window.URL.createObjectURL(blob);
      const dirSlug = (selectedCompany?.directors || []).find((d) => d.din === directorDin)?.name
        ?.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 30) || "director";
      const coSlug = (selectedCompany?.name || "company").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 30);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `${formKey.toUpperCase()}_${coSlug}_${dirSlug}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(dl);
      toast.success(`${formKey.toUpperCase()} ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-zinc-200" data-testid={`procedure-forms-${procedure.key}`}>
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} className="text-[#002FA7]" />
        <div className="label-eyebrow">Generate prefilled forms</div>
      </div>
      <p className="text-xs text-zinc-600 mb-3 max-w-2xl">
        Auto-fills company &amp; director info from your master record. Fields that need
        manual entry (age, father's name, witness names, notary, dates) are highlighted
        <span className="bg-yellow-200 px-1.5 mx-1">in yellow</span> for easy spotting.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
            Company
          </label>
          <select
            data-testid={`proc-form-co-${procedure.key}`}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none bg-white"
          >
            <option value="">— Pick a company —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {selectedCompany && (selectedCompany.directors || []).length > 0 && (
          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
              Signing director
            </label>
            <select
              data-testid={`proc-form-dir-${procedure.key}`}
              value={directorDin}
              onChange={(e) => setDirectorDin(e.target.value)}
              className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none bg-white"
            >
              {(selectedCompany.directors || []).filter((d) => d.din).map((d) => (
                <option key={d.din} value={d.din}>{d.name} · DIN {d.din}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {supported.map((rocCode) => {
          const spec = PROCEDURE_FORM_REGISTRY[rocCode];
          return (
            <div key={spec.key} className="border border-zinc-200 p-3 bg-white">
              <div className="text-sm font-medium text-zinc-900 mb-2">{spec.label}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!companyId || busy === `${spec.key}_docx`}
                  onClick={() => handleDownload(spec.key, "docx")}
                  data-testid={`proc-form-${spec.key}-docx-${procedure.key}`}
                  className="flex-1 swiss-btn-ghost text-xs disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {busy === `${spec.key}_docx` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  Word (.docx)
                </button>
                <button
                  type="button"
                  disabled={!companyId || busy === `${spec.key}_pdf`}
                  onClick={() => handleDownload(spec.key, "pdf")}
                  data-testid={`proc-form-${spec.key}-pdf-${procedure.key}`}
                  className="flex-1 swiss-btn-ghost text-xs disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {busy === `${spec.key}_pdf` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  PDF
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="border border-zinc-200 p-2 bg-white">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-[11px] leading-relaxed">{value}</div>
    </div>
  );
}
